import React, { useEffect, useMemo, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'

import { getCarrierVehiclesRequest } from '@/store/modules/users/vehicles/reducer'
import useListState from '@/hooks/useListState'

import {
  Box,
  Button,
  IconButton,
  LinearProgress,
  Table,
  TableBody,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
} from '@material-ui/core'
import { Edit as EditIcon, Add as AddIcon } from '@material-ui/icons'

import formatter from '@/services/formatter'

import { StyledTableContainer, StyledTableCell as TableCell } from '../styles'
import { VehicleFormEdit } from './FormEdit'
import { createVehicle, updateVehicle } from '@/api/users/vehicles'
import { Status } from '@/components/Status'

// -------------------------------------------------

export function VehiclesList() {
  const { vehicles, loading: vehiclesLoading } = useSelector(
    (state) => state.users.vehicles
  )

  const dispatch = useDispatch()

  const vehiclesList = useListState(vehicles)

  const [searchTerm, setSearchTerm] = useState('')
  const [currentVehicle, setCurrentVehicle] = useState(null)
  const [dialogOpen, setDialogOpen] = useState(false)

  const filteredVehicles = useMemo(() => {
    if (searchTerm.trim().length === 0) return vehiclesList.list

    return vehiclesList.list.filter((vehicle) =>
      [vehicle.wev_placa, vehicle.truck?.dsc_tipo_caminhao]
        .join('')
        .toLowerCase()
        .includes(searchTerm.toLowerCase())
    )
  }, [vehiclesList.list, searchTerm])

  function handleChangeSearch(event) {
    setSearchTerm(event.target.value)
  }

  function handleNew() {
    setDialogOpen(true)
    setCurrentVehicle({})
  }

  function handleEdit(vehicle) {
    setDialogOpen(true)
    setCurrentVehicle(vehicle)
  }

  function handleClose() {
    setDialogOpen(false)
    setCurrentVehicle(null)
  }

  async function handleSave(data) {
    if (currentVehicle?.id) {
      const updatedVehicle = await updateVehicle(currentVehicle.id, data)

      if (updatedVehicle) {
        vehiclesList.updateItem(currentVehicle.id, updatedVehicle)
        handleClose()
      }
    } else {
      const newDriver = await createVehicle(data)

      if (newDriver) {
        vehiclesList.addItem(newDriver)
        handleClose()
      }
    }
  }

  async function loadVehicles() {
    dispatch(getCarrierVehiclesRequest())
  }

  useEffect(() => {
    loadVehicles()
  }, [])

  useEffect(() => {
    vehiclesList.setList(vehicles)
  }, [vehicles, vehiclesList.setList])

  return (
    <Box>
      <VehicleFormEdit
        open={dialogOpen}
        current={currentVehicle}
        onSave={handleSave}
        onClose={handleClose}
      />

      <Box marginBottom={2} display="flex" alignItems="center" gridGap="1rem">
        <Box flex={1}>
          <TextField
            fullWidth
            placeholder="Pesquisar"
            variant="outlined"
            size="small"
            //
            value={searchTerm}
            onChange={handleChangeSearch}
          />
        </Box>

        <Button
          onClick={handleNew}
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
        >
          Adicionar caminhão
        </Button>
      </Box>

      {vehiclesLoading && vehicles.length === 0 && (
        <LinearProgress variant="indeterminate" />
      )}

      <StyledTableContainer>
        <Table size="medium">
          <TableHead>
            <TableRow>
              <TableCell width="20%">Placa</TableCell>
              <TableCell width="50%">Tipo do Caminhão</TableCell>
              <TableCell align="right" width="20%">
                Capacidade
              </TableCell>
              <TableCell align="center" width="10%">
                Status
              </TableCell>
              <TableCell width="5%" />
            </TableRow>
          </TableHead>

          <TableBody>
            {filteredVehicles.map((vehicle) => {
              return (
                <TableRow key={vehicle.id}>
                  <TableCell>{vehicle.wev_placa}</TableCell>
                  <TableCell>{vehicle.truck?.dsc_tipo_caminhao}</TableCell>
                  <TableCell align="right">
                    {formatter(vehicle.truck?.cam_pbt).toDecimal()}
                  </TableCell>

                  <TableCell align="center">
                    {vehicle.wev_ativo ? (
                      <Status
                        size="small"
                        mode="slim"
                        label="Ativo"
                        className="MuiChip-filledSuccess"
                      />
                    ) : (
                      <Status
                        size="small"
                        mode="slim"
                        label="Inativo"
                        className="MuiChip-filledError"
                      />
                    )}
                  </TableCell>

                  <TableCell align="center">
                    <Box display="flex" alignItems="center" gridGap="1rem">
                      <Tooltip title="Editar véiculo">
                        <IconButton
                          onClick={() => handleEdit(vehicle)}
                          size="small"
                          color="primary"
                        >
                          <EditIcon size={20} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              )
            })}

            {vehiclesList.list.length === 0 && (
              <TableRow>
                <TableCell align="center" colSpan={5}>
                  {vehiclesLoading
                    ? 'Buscando caminhões...'
                    : 'Nenhum caminhão cadastrado. Clique em Adicionar caminhão'}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </StyledTableContainer>
    </Box>
  )
}
